import { store } from '../store';
import { setGraphQLData } from '../graphqlSlice';
import { Charge } from '@/components/company/types/planTypes';
import { GraphQLCompanyData } from '../graphqlSlice';
import { logStateChange } from '@/utils/debugUtils';

/**
 * Updates the Redux store after a charge mapping is toggled (added or removed)
 * This ensures UI is immediately updated without requiring a page refresh
 *
 * @param chargeId The ID of the charge whose mapping was toggled
 * @param version The version the charge was toggled in
 * @param isAdded Whether the charge was added (true) or removed (false)
 */
export const updateChargeMappingInRedux = (chargeId: string, version: string, isAdded: boolean): void => {
  try {
    // Get current store state
    const currentState = store.getState();
    const currentGraphQLData = currentState.graphql.data;
    
    if (!currentGraphQLData || !currentGraphQLData.pages) {
      console.log('❌ [Redux] Cannot update charge mapping: No GraphQL data in Redux store');
      return;
    }
    
    // Create a deep copy to avoid direct state mutation
    const updatedData: GraphQLCompanyData = JSON.parse(JSON.stringify(currentGraphQLData));
    let mappingUpdated = false;

    // Find the charge and update its version mappings
    if (updatedData.pages) {
      // Iterate through pages
      updatedData.pages.forEach(page => {
        // 1. UPDATE CHARGE VERSIONS ARRAY
        if (page.plans) {
          // Iterate through plans
          page.plans.forEach(plan => {
            if (plan.charges) {
              // Find the specific charge
              const charge = plan.charges.find((c: Charge) => c.id === chargeId);
              
              if (charge) {
                console.log(`✅ [Redux] ${isAdded ? 'Adding' : 'Removing'} charge ${chargeId} ${isAdded ? 'to' : 'from'} version ${version} in Redux store`);
                
                // Ensure versions array exists
                if (!charge.versions) {
                  charge.versions = [];
                }
                
                // Update the charge's versions array based on whether we're adding or removing
                if (isAdded && !charge.versions.includes(version)) {
                  // Add version to the versions array
                  charge.versions.push(version);
                  mappingUpdated = true;
                  console.log(`✅ [Redux] Added version ${version} to charge ${chargeId}`);
                } else if (!isAdded && charge.versions.includes(version)) {
                  // Remove version from the versions array
                  charge.versions = charge.versions.filter(v => v !== version);
                  mappingUpdated = true;
                  console.log(`✅ [Redux] Removed version ${version} from charge ${chargeId}`);
                }
                
                // Log the updated versions array for debugging
                console.log(`📊 [Redux] Charge ${chargeId} versions after update:`, charge.versions);
              }
            }
          });
        }
        
        // 2. CRITICAL: UPDATE QUARTERS DATA STRUCTURE
        if (page.quarters) {
          // Find the quarter that corresponds to this version
          let targetQuarter = page.quarters.find(q => q.version === version);
          
          // If no exact version match, try to find by inferring quarter from version
          if (!targetQuarter && version) {
            // Extract year and month from version (e.g., "20230416" -> "2023Q2")
            const yearFromVersion = version.substring(0, 4);
            const monthFromVersion = parseInt(version.substring(4, 6));
            let inferredQuarter = "";
            
            // Map month to quarter
            if (monthFromVersion >= 1 && monthFromVersion <= 3) {
              inferredQuarter = `${yearFromVersion}Q1`;
            } else if (monthFromVersion >= 4 && monthFromVersion <= 6) {
              inferredQuarter = `${yearFromVersion}Q2`;
            } else if (monthFromVersion >= 7 && monthFromVersion <= 9) {
              inferredQuarter = `${yearFromVersion}Q3`;
            } else {
              inferredQuarter = `${yearFromVersion}Q4`;
            }
            
            // Find by inferred quarter
            targetQuarter = page.quarters.find(q => q.quarter === inferredQuarter);
          }
          
          if (targetQuarter) {
            // Ensure the ids array exists
            if (!targetQuarter.ids) {
              targetQuarter.ids = [];
            }
            
            // Update the ids array based on whether we're adding or removing
            if (isAdded && !targetQuarter.ids.includes(chargeId)) {
              // Add charge ID to the quarter's ids array
              targetQuarter.ids.push(chargeId);
              mappingUpdated = true;
              console.log(`✅ [Redux] Added charge ${chargeId} to quarter ${targetQuarter.quarter} ids array`);
            } else if (!isAdded && targetQuarter.ids.includes(chargeId)) {
              // Remove charge ID from the quarter's ids array
              targetQuarter.ids = targetQuarter.ids.filter(id => id !== chargeId);
              mappingUpdated = true;
              console.log(`✅ [Redux] Removed charge ${chargeId} from quarter ${targetQuarter.quarter} ids array`);
            }
          } else {
            console.log(`⚠️ [Redux] Could not find matching quarter for version ${version}`);
          }
        }
      });
    }

    if (mappingUpdated) {
      // Update the Redux store with our modified data
      store.dispatch(setGraphQLData(updatedData));
      console.log('✅ [Redux] GraphQL store updated with new charge mapping');
      
      // Verify that the update was successful by checking key data structures
      const afterState = store.getState().graphql.data;
      if (afterState) {
        // Count how many quarters have this charge in their ids array
        let quartersWithCharge = 0;
        let chargeInVersions = false;
        
        afterState.pages?.forEach(page => {
          // Check if charge exists in any quarter's ids array
          page.quarters?.forEach(quarter => {
            if (quarter.ids?.includes(chargeId)) {
              quartersWithCharge++;
              console.log(`🔍 [Redux] Verified: Charge ${chargeId} is in quarter ${quarter.quarter} (${quarter.version})`);
            }
          });
          
          // Check if charge has version in its versions array
          page.plans?.forEach(plan => {
            const charge = plan.charges?.find((c: any) => c.id === chargeId);
            if (charge && charge.versions?.includes(version)) {
              chargeInVersions = true;
              console.log(`🔍 [Redux] Verified: Version ${version} is in charge ${chargeId} versions array`);
            }
          });
        });
        
        console.log(`📊 [Redux] Verification: Charge ${chargeId} appears in ${quartersWithCharge} quarters and ${chargeInVersions ? 'has' : 'does not have'} the version in its versions array`);
      }
      
    } else {
      console.log('❓ [Redux] Could not find charge to update mapping in Redux store');
      console.log('🔎 Looking for charge with ID:', chargeId);
      console.log('🔎 For version:', version);
    }
  } catch (error) {
    console.error('❌ [Redux] Error updating charge mapping in Redux store:', error);
  }
};

/**
 * Updates a charge in the Redux store after a GraphQL mutation
 * This ensures UI is immediately updated without requiring a full refetch
 *
 * @param updatedCharge The charge data returned from the GraphQL mutation
 */
export const updateChargeInRedux = (updatedCharge: Charge): void => {
  try {
    // Get current store state
    const currentState = store.getState();
    const currentGraphQLData = currentState.graphql.data;
    
    if (!currentGraphQLData || !currentGraphQLData.pages) {
      console.log('❌ [Redux] Cannot update charge: No GraphQL data in Redux store');
      console.log('🔍 [Redux] Current state structure:', {
        hasGraphQLData: !!currentGraphQLData,
        hasPages: !!currentGraphQLData?.pages,
        stateKeys: currentGraphQLData ? Object.keys(currentGraphQLData) : 'none'
      });
      return;
    }
    
    // Create a deep copy to avoid direct state mutation
    const updatedData: GraphQLCompanyData = JSON.parse(JSON.stringify(currentGraphQLData));
    let chargeUpdated = false;
    let chargeBeforeUpdate: Charge | null = null;

    // Find the charge and update it
    if (updatedData.pages) {
      // Iterate through pages
      updatedData.pages.forEach(page => {
        if (page.plans) {
          // Iterate through plans
          page.plans.forEach(plan => {
            if (plan.charges) {
              // Find and update the specific charge
              const chargeIndex = plan.charges.findIndex(
                (charge: Charge) => charge.id === updatedCharge.id
              );
              
              if (chargeIndex !== -1) {
                // Store the charge before updating for debugging
                chargeBeforeUpdate = {...plan.charges[chargeIndex]};
                
                console.log(`✅ [Redux] Updating charge ${updatedCharge.id} in Redux store`);
                console.log('Found in plan:', plan.name || 'Unnamed plan');
                
                // Log detailed state change for debugging
                logStateChange(
                  `Charge ${updatedCharge.id} Update`, 
                  chargeBeforeUpdate, 
                  updatedCharge
                );
                
                // Update the charge
                plan.charges[chargeIndex] = updatedCharge;
                chargeUpdated = true;
              }
            }
          });
        }
      });
    }

    if (chargeUpdated) {
      // Add some helpful debug info about what's being updated
      console.log('✅ [Redux] Charge found. Updating Redux store with:', {
        chargeId: updatedCharge.id,
        pricingMetricId: updatedCharge.pricing_metric?.id,
        price: updatedCharge.price,
        type: updatedCharge.type
      });
      
      // Update the Redux store with our modified data
      store.dispatch(setGraphQLData(updatedData));
      console.log('✅ [Redux] GraphQL store updated with new charge data');
    } else {
      console.log('❓ [Redux] Could not find charge to update in Redux store');
      console.log('🔎 Looking for charge with ID:', updatedCharge.id);
      console.log('📊 Available GraphQL data structure:', {
        pagesCount: currentGraphQLData.pages?.length,
        hasPlans: currentGraphQLData.pages?.some(p => p.plans && p.plans.length > 0)
      });
    }
  } catch (error) {
    console.error('❌ [Redux] Error updating charge in Redux store:', error);
  }
};

/**
 * Removes a charge from the Redux store after a successful GraphQL deletion mutation
 * This ensures UI is immediately updated without requiring a full refetch
 *
 * @param chargeId The ID of the charge that was deleted
 */
export const removeChargeFromRedux = (chargeId: string): void => {
  try {
    // Get current store state
    const currentState = store.getState();
    const currentGraphQLData = currentState.graphql.data;
    
    if (!currentGraphQLData || !currentGraphQLData.pages) {
      console.log('❌ [Redux] Cannot remove charge: No GraphQL data in Redux store');
      return;
    }
    
    // Create a deep copy to avoid direct state mutation
    const updatedData: GraphQLCompanyData = JSON.parse(JSON.stringify(currentGraphQLData));
    let chargeRemoved = false;

    // Find the charge and remove it
    if (updatedData.pages) {
      // Iterate through pages
      updatedData.pages.forEach(page => {
        if (page.plans) {
          // Iterate through plans
          page.plans.forEach(plan => {
            if (plan.charges && plan.charges.length > 0) {
              // Find the charge index
              const chargeIndex = plan.charges.findIndex(
                (charge: Charge) => charge.id === chargeId
              );
              
              if (chargeIndex !== -1) {
                console.log(`✅ [Redux] Removing charge ${chargeId} from plan ${plan.name || plan.id} in Redux store`);
                
                // Store the original charge array for debugging
                const originalCharges = [...plan.charges];
                
                // Remove the charge
                plan.charges.splice(chargeIndex, 1);
                chargeRemoved = true;
                
                // Log the change
                logStateChange(
                  `Plan Charges (${plan.name || plan.id})`,
                  originalCharges,
                  plan.charges
                );
              }
            }
          });
        }
      });
    }

    if (chargeRemoved) {
      // Update the Redux store with our modified data
      store.dispatch(setGraphQLData(updatedData));
      console.log('✅ [Redux] GraphQL store updated after charge removal');
    } else {
      console.log('❓ [Redux] Could not find charge to remove in Redux store');
      console.log('🔎 Looking for charge with ID:', chargeId);
    }
  } catch (error) {
    console.error('❌ [Redux] Error removing charge from Redux store:', error);
  }
};
